/**
 * FormatPicker.tsx — a tiny format-selection modal for Download / Export.
 *
 * Replaces the old flat list of "Download bundle (JSON) / Download project
 * (ZIP) / Export run → CSV (wide) / …" entries in the File menu. Instead,
 * "Download…" and "Export…" each open this picker so the user picks the
 * format they want from a focused list — same look + keyboard model as the
 * LibraryModal picker (↑/↓ move, Enter select, Esc close).
 *
 *   ┌───────────────────────────────────────┐
 *   │ Download                              │
 *   ├───────────────────────────────────────┤
 *   │  ▸ Project (ZIP)                      │
 *   │    graph + manifest + code            │
 *   │  ▸ Bundle (JSON)                      │
 *   │    the graph definition only          │
 *   └───────────────────────────────────────┘
 */
import { useEffect, useRef, useState } from 'react';

export interface FormatOption {
  id: string;
  label: string;
  description?: string;
  disabled?: boolean;
  disabledReason?: string;
}

interface FormatPickerProps {
  isOpen: boolean;
  title: string;
  options: FormatOption[];
  onSelect: (id: string) => void;
  onClose: () => void;
}

export function FormatPicker({
  isOpen,
  title,
  options,
  onSelect,
  onClose,
}: FormatPickerProps): JSX.Element | null {
  const [active, setActive] = useState(0);
  const listRef = useRef<HTMLDivElement | null>(null);

  // Reset to the first pickable option whenever the picker (re)opens or the
  // option list changes.
  useEffect(() => {
    if (!isOpen) return;
    const firstPickable = options.findIndex((o) => !o.disabled);
    setActive(firstPickable >= 0 ? firstPickable : 0);
  }, [isOpen, options]);

  // Keep the active row scrolled into view.
  useEffect(() => {
    if (!isOpen) return;
    const row = listRef.current?.querySelector<HTMLElement>(`[data-idx="${active}"]`);
    row?.scrollIntoView({ block: 'nearest' });
  }, [active, isOpen]);

  if (!isOpen) return null;

  const clamp = (i: number) => {
    const max = options.length - 1;
    return Math.max(0, Math.min(i, max));
  };
  const nextPickable = (dir: 1 | -1) => {
    let i = active;
    for (let k = 0; k < options.length; k++) {
      i = clamp(i + dir);
      if (!options[i].disabled) return i;
    }
    return active;
  };

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive(nextPickable(1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive(nextPickable(-1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const opt = options[active];
      if (opt && !opt.disabled) {
        onSelect(opt.id);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="picker format-picker"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={onKey}
      >
        <header className="picker-header">
          <span className="format-picker-title">{title}</span>
          <button type="button" className="picker-close" aria-label="Close" onClick={onClose}>
            ✕
          </button>
        </header>
        <div className="picker-body" ref={listRef}>
          <ul className="picker-list" role="listbox">
            {options.map((opt, i) => (
              <li
                key={opt.id}
                data-idx={i}
                role="option"
                aria-selected={i === active}
                className={`picker-row${i === active ? ' selected' : ''}${opt.disabled ? ' disabled' : ''}`}
                onMouseEnter={() => !opt.disabled && setActive(i)}
                onClick={() => {
                  if (!opt.disabled) onSelect(opt.id);
                }}
              >
                <span className="picker-row-main">
                  <span className="picker-row-title">{opt.label}</span>
                  {opt.description && (
                    <span className="picker-row-desc">
                      {opt.disabled && opt.disabledReason ? opt.disabledReason : opt.description}
                    </span>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </div>
        <footer className="picker-footer">
          <span className="hint">
            <kbd>↑</kbd> <kbd>↓</kbd> navigate · <kbd>↵</kbd> select · <kbd>Esc</kbd> close
          </span>
        </footer>
      </div>
    </div>
  );
}
